<template>
    <span
        v-tooltip:[tooltipModifier]="tooltipValue"
        v-bind="$attrs"
        :class="tooltipClasses"
        :tabindex="focusableTabIndex"
        @focus="showOnFocus"
        @blur="hideOnBlur"
        @focusin="showOnFocusIn"
        @focusout="hideOnFocusOut"
        @pointerdown="rememberPointerType"
        @click="showOnClick"
        @mouseleave.capture="keepTappedTooltipOpen"
    >
        <slot />
    </span>
</template>

<script>
/**
 * ETooltip - Abstraction wrapper to replicate Buefy's b-tooltip
 *
 * Usage:
 *   <e-tooltip label="This is a helpful tooltip">
 *     <e-button>Hover me</e-button>
 *   </e-tooltip>
 *
 *   <e-tooltip label="Delete this item" position="is-bottom">
 *     <e-icon icon="trash" />
 *   </e-tooltip>
 *
 *   <e-tooltip label="Long text here" multilined size="is-medium">
 *     <e-icon icon="info" />
 *   </e-tooltip>
 *
 */

const MULTILINE_WIDTHS = {
    'is-small': 'var(--tooltip-multiline-small)',
    'is-medium': 'var(--tooltip-multiline-medium)',
    'is-large': 'var(--tooltip-multiline-large)',
};

const resolveMultilineWidth = size => MULTILINE_WIDTHS[size] || MULTILINE_WIDTHS['is-small'];

export default {
    name: 'ETooltip',
    inheritAttrs: false,

    props: {
        label: {
            type: String,
            default: undefined,
        },

        position: {
            type: String,
            default: 'is-top',
        },

        multilined: {
            type: Boolean,
            default: false,
        },

        type: {
            type: String,
            default: undefined,
        },

        size: {
            type: String,
            default: undefined,
        },

        delay: {
            type: Number,
            default: 0,
        },

        active: {
            type: Boolean,
            default: true,
        },

        dashed: {
            type: Boolean,
            default: false,
        },

        tooltipClass: {
            type: String,
            default: undefined,
        },

        /**
         * Makes the trigger keyboard reachable and shows the tooltip on focus.
         * Opt-in, so existing hover-only tooltips are unaffected. Pair it with
         * an `aria-label` when the trigger is an icon with no text of its own.
         */
        focusable: {
            type: Boolean,
            default: false,
        },

        /**
         * When the slot is a native control (for example a `<button>`), keep that
         * element in the tab order instead of the wrapper span. Pair with `focusable`.
         */
        interactiveTrigger: {
            type: Boolean,
            default: false,
        },
    },

    emits: [],

    computed: {
        tooltipClasses() {
            const classes = ['tw:xl:max-w-fit'];

            if (this.dashed) {
                classes.push(
                    'tw:border-b',
                    'tw:border-dashed',
                    'tw:border-[var(--tooltip-dashed-border)]',
                    'tw:cursor-default',
                    'tw:inline-block'
                );
            }

            return classes.join(' ');
        },

        tooltipPosition() {
            const buefyPosition = this.position || '';

            const mapBuefyPositionToPrimeVuePosition = {
                'is-top': 'top',
                'is-bottom': 'bottom',
                'is-left': 'left',
                'is-right': 'right',
            };

            const defaultBuefyPosition = 'top';
            return mapBuefyPositionToPrimeVuePosition[buefyPosition] ?? defaultBuefyPosition;
        },

        tooltipModifier() {
            return { position: this.tooltipPosition };
        },

        tooltipValue() {
            const pt = {
                text: {
                    class: this.tooltipTextClasses,
                    style: this.tooltipTextStyle,
                },
            };

            if (this.multilined) {
                // The Aura theme caps `.p-tooltip` (the positioning root) at max-width: 12.5rem
                // (200px). A multilined tooltip sets an explicit width on the inner text, so any
                // size wider than 200px (is-medium/is-large) overhangs its own root — PrimeVue then
                // positions the narrower root while the visible box pokes past it (and off-screen
                // near a viewport edge). Match the root to the text width so positioning and the
                // visible box align, letting PrimeVue's built-in edge clamping work correctly.
                pt.root = { style: this.tooltipRootStyle };
            }

            return {
                value: this.label || '',
                disabled: this.active === false,
                pt,
                showDelay: this.delay || 0,
            };
        },

        tooltipTextClasses() {
            const classes = ['tw:text-sm'];

            if (this.multilined) {
                classes.push('tw:text-center');
            }

            return classes.join(' ');
        },

        tooltipRootStyle() {
            // Lift the theme's 12.5rem (200px) cap and pin the positioning root to the same width
            // as the text box, so the root PrimeVue measures/positions matches what the user sees.
            const width = resolveMultilineWidth(this.size);

            return {
                width,
                'max-width': width,
            };
        },

        focusableTabIndex() {
            if (!this.focusable || this.interactiveTrigger) {
                return undefined;
            }

            return 0;
        },

        tooltipTextStyle() {
            const style = {};

            if (this.multilined) {
                const width = resolveMultilineWidth(this.size);

                style.width = width;
                style['max-width'] = width;
                style['white-space'] = 'normal';
                style['text-align'] = 'center';
                style['text-wrap'] = 'balance';
                style['line-height'] = 'var(--tooltip-line-height)';
            }

            return style;
        },
    },

    created() {
        /** Non-reactive: the pointer that started the current click, and the outside-tap release. */
        this.lastPointerType = null;
        this.releaseTappedTooltip = null;
    },

    beforeUnmount() {
        this.releaseTappedTooltip?.();
    },

    methods: {
        /**
         * PrimeVue's tooltip directive binds hover XOR focus (see `bindEvents`
         * in primevue/tooltip), so making the trigger focusable would otherwise
         * trade the hover tooltip away. Re-dispatching the mouse events it does
         * listen for keeps both. `autoHide` defaults to true, so a synthetic
         * mouseleave hides unconditionally.
         */
        revealTooltip(event) {
            event.currentTarget.dispatchEvent(new MouseEvent('mouseenter'));
        },

        dismissTooltip(event) {
            event.currentTarget.dispatchEvent(new MouseEvent('mouseleave'));
        },

        showOnFocus(event) {
            if (this.focusable && !this.interactiveTrigger) {
                this.revealTooltip(event);
            }
        },

        hideOnBlur(event) {
            if (this.focusable && !this.interactiveTrigger) {
                this.dismissTooltip(event);
            }
        },

        showOnFocusIn(event) {
            if (this.focusable && this.interactiveTrigger) {
                this.revealTooltip(event);
            }
        },

        hideOnFocusOut(event) {
            if (!this.focusable || !this.interactiveTrigger) {
                return;
            }

            const root = event.currentTarget;

            if (event.relatedTarget && root.contains(event.relatedTarget)) {
                return;
            }

            this.releaseTappedTooltip?.();
            this.dismissTooltip(event);
        },

        /**
         * Touch has no hover, so a tap must open the tooltip. PrimeVue's directive hides the
         * tooltip on every click of this element, and its listener runs after this one, so a
         * synchronous reveal is undone at once. Reveal after the click has finished instead.
         * Only interactive triggers do this; other focusable tooltips keep the directive's
         * hide-on-click.
         */
        showOnClick(event) {
            if (!this.focusable || !this.interactiveTrigger) {
                return;
            }

            const trigger = event.currentTarget;

            if (this.lastPointerType === 'touch') {
                this.keepOpenUntilOutsideTap(trigger);
            }

            this.lastPointerType = null;

            setTimeout(() => trigger.dispatchEvent(new MouseEvent('mouseenter')), 0);
        },

        rememberPointerType(event) {
            this.lastPointerType = event.pointerType;
        },

        /**
         * A browser can send a real mouseleave right after a tap ends, which would close a
         * tooltip the learner just opened. After a touch tap, keep it open until the next tap
         * outside the trigger, or until focus leaves it.
         */
        keepOpenUntilOutsideTap(trigger) {
            this.releaseTappedTooltip?.();

            const onOutsidePointerDown = event => {
                if (trigger.contains(event.target)) {
                    return;
                }

                this.releaseTappedTooltip?.();
                trigger.dispatchEvent(new MouseEvent('mouseleave'));
            };

            document.addEventListener('pointerdown', onOutsidePointerDown, true);

            this.releaseTappedTooltip = () => {
                document.removeEventListener('pointerdown', onOutsidePointerDown, true);
                this.releaseTappedTooltip = null;
            };
        },

        /** Runs before the directive's own mouseleave listener, so it can keep a tapped tooltip open. */
        keepTappedTooltipOpen(event) {
            if (this.releaseTappedTooltip) {
                event.stopImmediatePropagation();
            }
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';
@import 'resources/scss/_variables.scss';
@import 'resources/scss/_tooltip-variables.scss';

:root {
    --tooltip-primary-bg: #{$primary};
    --tooltip-primary-text: #{$white};
    --tooltip-info-bg: #{$info};
    --tooltip-info-text: #{$white};
    --tooltip-success-bg: #{$success};
    --tooltip-success-text: #{$white};
    --tooltip-warning-bg: #{$warning};
    --tooltip-warning-text: #{rgba($black, 0.7)};
    --tooltip-danger-bg: #{$danger};
    --tooltip-danger-text: #{$white};
    --tooltip-dark-bg: #{$black};
    --tooltip-dark-text: #{$white};
    --tooltip-light-bg: #{$grey-lightest};
    --tooltip-light-text: #{rgba($black, 0.7)};
    --tooltip-dashed-border: #{$grey-light};
    --tooltip-multiline-small: #{$tooltip-multiline-small};
    --tooltip-multiline-medium: #{$tooltip-multiline-medium};
    --tooltip-multiline-large: #{$tooltip-multiline-large};
    --tooltip-line-height: #{$tooltip-line-height};
}
</style>
